<script>
/* 构建期注入(见 vite.config.mjs 的 define):模块级常量,defineProps 可引用。
 * 常量 __VITE_PROXY_TARGET__ 取自 .env.<mode> 的 VITE_PROXY_TARGET:
 *  - npm run dev    → .env.development(http://dev.keeson.com/ergo-advisor-back)
 *  - npm run build  → .env.production(https://ergoadvisor.ergomotion.com/ergo-advisor-back)
 * 两种 mode 注入的都是完整地址,因此默认请求直连后端(需后端放行 CORS);
 * vite.config.mjs 的 server.proxy 仅对相对路径 /api 生效,默认值不会走它。
 * 宿主页面仍可通过 attribute / mount() props 覆盖(传相对路径即可走代理)。 */
const DEFAULT_API_URL = __VITE_PROXY_TARGET__

/* 预设:quiz 四步问卷题库(对齐 demo intake-overlay.js 的 QUESTIONS 与 quiz-definition.json)
 * Q1 多选、Q2 条件单选(仅当 Q1 勾选触发项时才出现)、Q3 单选带选项描述、Q4 多选含互斥项
 * 选项含:id(上报后端用的稳定标识,对齐 demo quiz-definition.json 的 option id)/
 * label(UI 长文案,界面与标签卡中均以 label 为键)/ chip(气泡标签卡短标签)/
 * desc(选项下方小字说明,可选)/ exclusive(互斥项,选中即清空同题其他) */
const QUIZ_TIRED_LABEL = "I don't know why I wake up tired"
const QUIZ_ROOM_NONE_LABEL = 'None of these — straight shot to the bedroom'

const QUIZ_STEPS = [
  {
    key: 'sleep_change',
    title: 'What would you change about how you sleep?',
    type: 'multi',
    hint: 'Pick as many as you like',
    options: [
      { id: 'reading', label: 'I read, watch TV, or work in bed', chip: 'Read / TV' },
      { id: 'back_hips', label: 'My back or hips ache in the morning', chip: 'Back / hips' },
      { id: 'snoring', label: "Snoring — mine or my partner's", chip: 'Snoring' },
      { id: 'tired', label: QUIZ_TIRED_LABEL, chip: 'Wake tired' },
      { id: 'separate_controls', label: 'We each want the bed set differently', chip: 'Different settings' },
      { id: 'storage', label: 'I need the space under the bed', chip: 'Under-bed space' },
      { id: 'planning_ahead', label: "We're planning ahead to stay in this house", chip: 'Planning ahead' },
      { id: 'nicer_bed', label: "Nothing's wrong — I just want a nicer bed", chip: 'Nicer bed' },
    ],
  },
  {
    key: 'sleep_measure',
    title: 'Do you want the bed to measure your sleep, or just position you?',
    type: 'single',
    hint: 'Single select',
    // 条件题:仅当 Q1 勾选「wake up tired」时进入可见题序列
    showWhen: { key: 'sleep_change', label: QUIZ_TIRED_LABEL },
    options: [
      { id: 'measure', label: 'Measure it and tell me what it finds', chip: 'Measure sleep' },
      { id: 'position', label: 'Just position me', chip: 'Position only' },
    ],
  },
  {
    key: 'how_far',
    title: 'How far do you want the bed to go?',
    type: 'single',
    hint: 'Single select',
    options: [
      {
        id: 'essentials',
        label: 'The essentials, done well',
        desc: 'Head and foot move, quiet motors, folds for delivery',
        chip: 'Essentials',
      },
      {
        id: 'comfort',
        label: 'Add the comfort features',
        desc: 'Lumbar support, massage, under-bed lighting, charging at the bed',
        chip: 'Comfort',
      },
      {
        id: 'more',
        label: 'The ones that do more',
        desc: 'Sleep tracking, wall hugger, raises and lowers, storage underneath',
        chip: 'Do more',
      },
      {
        id: 'best',
        label: 'Show me the best you make',
        desc: 'Everything, with the finish to match',
        chip: 'Best',
      },
      { id: 'range', label: "I'd rather see the range first", chip: 'See the range' },
    ],
  },
  {
    key: 'room_access',
    title: 'Anything tricky about getting it into the room?',
    type: 'multi',
    hint: 'Pick all that apply · this one is a hard filter',
    options: [
      { id: 'stairs', label: 'Stairs, a tight corner, or a narrow door', chip: 'Stairs / tight turn' },
      { id: 'existing_frame', label: 'It has to drop into the bed frame I already have', chip: 'Existing frame' },
      { id: 'no_assembly', label: "I'd rather not assemble anything", chip: 'No assembly' },
      { id: 'none', label: QUIZ_ROOM_NONE_LABEL, chip: 'Straight shot', exclusive: true },
    ],
  },
]

/** 上报后端的问卷版本号(固定值,随 chat-messages 的 intake.quiz_version 发出) */
const QUIZ_VERSION = 'p1.1'

/** Bed size 可选项(对齐 demo bridge 的 sizeOrder);提交时随结构化 intake 携带 */
const QUIZ_SIZE_OPTIONS = ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'Split King', 'California King', 'Split California King']
</script>

<script setup>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import logoUrl from './assets/logo.svg'

/* 富文本中的链接(裸 URL / markdown 链接)点击时新开页面跳转,不离开宿主页面
 * 必须用 DOMPurify 的 afterSanitizeAttributes hook:marked 输出的 target
 * 会被 DOMPurify 默认净化策略删除,净化后再补回才能生效 */
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank')
    node.setAttribute('rel', 'noopener noreferrer')
  }
})

const props = defineProps({
  /** 面板标题(顶栏) */
  title: { type: String, default: 'Ergo Advisor' },
  /** 门控页标题 */
  advisorName: { type: String, default: 'Ergo Advisor' },
  /** 门控页副标题 */
  advisorTagline: { type: String, default: 'A helpful and polite shopping advisor for Ergomotion.' },
  /** FAB 正上方的提示气泡文案 */
  bubbleText: {
    type: String,
    default:
      "👋 Hello! I'm ErgoAdvisor, your Ergomotion shopping assistant. I can help you explore adjustable beds - compare models, check features and sizes, and find the right fit.",
  },
  /** 免责声明(门控页下半部分,第一条) */
  disclaimer: {
    type: String,
    default:
      'This assistant may collect information from your chat—such as preferences, size needs, and product interests—to personalize recommendations and improve your shopping experience. Your data is used only to optimize Ergo Advisor and will not be sold to third parties.',
  },
  /** AI 免责声明(门控页下半部分,第二条) */
  aiDisclaimer: {
    type: String,
    default:
      'Ergo Advisor is powered by AI and may make mistakes. Please verify information on our website.',
  },
  /** 开局欢迎语(前端写死,不请求后端) */
  firstMessage: {
    type: String,
    default:
      "Hello! I'm Ergo Advisor, your Ergomotion shopping assistant. I can help you explore adjustable beds and smart beds—compare models, check features and sizes, and find the right fit.\n\nWhat would you like to know?",
  },
  /** 输入框锁定时的占位文案 */
  lockedPlaceholder: { type: String, default: 'Message...' },
  /** 输入框占位文案 */
  placeholder: { type: String, default: 'Message...' },
  /** chat-messages 端点(构建期注入默认值,可用标签 attribute 覆盖) */
  apiUrl: { type: String, default: DEFAULT_API_URL + '/api/v1/chat-messages' },
  /** intake-answers 端点(quiz 提交时并行上报,可用标签 attribute 覆盖) */
  intakeApiUrl: { type: String, default: DEFAULT_API_URL + '/api/v1/intake-answers' },
  /** 主题色(品牌蓝) */
  accent: { type: String, default: '#4795B8' },
  /** 超时分钟数,无新消息后自动结束会话 */
  timeoutMinutes: { type: Number, default: 30 },
  /** 请求超时秒数:响应头超过该时长未返回则中断(网络异常处理) */
  requestTimeout: { type: Number, default: 30 },
  /** 打字机速度:每个词输出的间隔 ms,越大越慢、流式感越明显 */
  typingSpeed: { type: Number, default: 10 },
  /** 是否允许部分作答即提交初始匹配(对齐 demo 的 partial_submit 开关):
   *  true 时末题在未答全的情况下显示「See initial fit」并可直接提交 */
  partialSubmit: { type: Boolean, default: true },
})

/* ================================ 状态 ================================ */
const open = ref(false) // 面板开合
const showBubble = ref(false) // 提示气泡
let bubbleRemaining = 6000 // 气泡 6s 倒计时(ms),悬停暂停时会修改,必须用 let
let bubbleStart = 0
let bubbleTimer = null

const consented = ref(false) // 是否已同意门控(内存态,End Chat 后重置)
const messages = ref([]) // { role, content, streaming, id }
const showQuizLink = ref(false) // 欢迎语下方 quiz 引导行是否显示
const showQuizDrawer = ref(false) // quiz 底部抽屉是否打开
const quizStep = ref(0) // quiz 当前步骤(可见题序列内的下标)
const quizAnswers = reactive({ sleep_change: [], sleep_measure: '', how_far: '', room_access: [] }) // quiz 各题作答
const quizSize = ref('') // 最后一题附加的 Bed size(空串=Not sure yet/未选)
const quizFinished = ref(false) // 是否已完成 Intake(真实发出);完成前入口保留,完成后本会话摘除
/** 可见题序列:条件题(showWhen)未满足条件时从序列中剔除,题数与进度随之变化 */
const quizVisibleSteps = computed(() =>
  QUIZ_STEPS.filter((s) => !s.showWhen || quizAnswers[s.showWhen.key].includes(s.showWhen.label))
)
/** 某题是否已作答(单选看非空、多选看非空数组) */
function quizStepPicked(step) {
  const v = quizAnswers[step.key]
  return step.type === 'multi' ? v.length > 0 : !!v
}
/** 当前步骤是否已有效(该题已选过,与 demo 的 Continue 门槛一致) */
const quizStepValid = computed(() => {
  const step = quizVisibleSteps.value[quizStep.value]
  return step ? quizStepPicked(step) : true
})
/** 已作答题数 / 是否全部作答 / 是否位于末题 */
const quizAnsweredCount = computed(() => quizVisibleSteps.value.filter((s) => quizStepPicked(s)).length)
const quizAllDone = computed(() => quizAnsweredCount.value === quizVisibleSteps.value.length)
const quizIsLastStep = computed(() => quizStep.value === quizVisibleSteps.value.length - 1)
/** 提交门槛:partialSubmit 下只要 1 题作答即可(初始匹配);否则需全部作答 */
const quizCanSubmit = computed(() =>
  props.partialSubmit ? quizAnsweredCount.value > 0 : quizAllDone.value
)
/** 主按钮文案:Continue(过程)/ See my match(答全)/ See initial fit(部分作答) */
const quizPrimaryLabel = computed(() => {
  if (!quizIsLastStep.value) return 'Continue'
  return props.partialSubmit && !quizAllDone.value ? 'See initial fit' : 'See my match'
})
/** 条件题增减导致当前下标越界时收敛回可选题范围 */
watch(quizVisibleSteps, (steps) => {
  if (quizStep.value > steps.length - 1) quizStep.value = Math.max(0, steps.length - 1)
})
/** quiz 引导行的锚定消息 id:URL 补播消息优先,无则取欢迎语;锚定不随对话下沉 */
const quizAnchorId = computed(() => {
  const urlMsg = messages.value.find((m) => m.urlAnswer)
  if (urlMsg) return urlMsg.id
  const welcomeMsg = messages.value.find((m) => m.welcome)
  return welcomeMsg ? welcomeMsg.id : null
})
const thinking = ref(false) // Thinking 加载态
const inputLocked = ref(true) // 欢迎语首段到达前锁定输入框(不可打字)
const sendBlocked = ref(true) // 欢迎语后等待 URL 回复/补播期间:可打字但暂缓发送
const showEndConfirm = ref(false) // End Chat 确认层
const showTimeoutBar = ref(false) // 超时新会话条
const input = ref('')
const sending = ref(false)
const aborter = ref(null)
const listEl = ref(null)
const inputEl = ref(null)

let conversationId = '' // 会话 id(刷新/End Chat 后重建)
let pageUrlPromise = null // 同意时上报页面 URL 的隐藏请求(在途标记:决定何时放行发送)
let pageUrlAborter = null // 上述请求的中断句柄(会话重置时中断,避免旧会话结果串入新会话)
let pendingUrlAnswer = '' // URL 隐藏请求的非 666 回复:暂存,欢迎语输出完后继续打字机输出
let welcomeFinished = false // 欢迎语是否已输出完(决定暂存回复何时补播)
const hasUrlAnswer = ref(false) // 后端是否带回欢迎语补播内容(决定欢迎语是否隐藏头像只留占位)

/** 前端生成随机 UUID 作为 request_id */
function genRequestId() {
  if (crypto.randomUUID) return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}
let msgSeq = 0
let timeoutHandle = null
let simTimer = null // 前端写死回复的延迟定时器(End Chat/新会话时需清理)

/* ==================== 前端逐词打字机(流式输出统一走这里) ==================== */
// 后端增量先进队列,前端按英文单词逐个展示(英文习惯)
// 错误话术(固定文案,不拼接具体错误细节)
const NETWORK_ERROR_TEXT = 'Network error. Please re-enter your message or refresh the conversation.'
const GENERIC_ERROR_TEXT = 'Something went wrong. Please try again in a moment.'
const tokenizer =
  typeof Intl !== 'undefined' && Intl.Segmenter ? new Intl.Segmenter('en', { granularity: 'word' }) : null
let tokenQueue = [] // 待展示的文本片段
let typeTimer = null // 打字机定时器
let streamEnded = false // 当前流是否已结束(等待队列输出完再收尾)
let currentMsg = null // 正在打字的气泡
let finishTypingCallback = null // 队列输出完后的收尾回调

function tokenize(text) {
  if (tokenizer) {
    // 按词切分(英文单词/空格/标点/中文词均作为独立片段,保持原文间距)
    return [...tokenizer.segment(text)].map((s) => s.segment)
  }
  // 兼容兜底:按空白切分,每个片段自带尾部空格
  return text.match(/\S+\s*/g) || (text ? [text] : [])
}

function startTyping() {
  if (typeTimer) return
  typeTimer = setInterval(() => {
    if (tokenQueue.length > 0) {
      const token = tokenQueue.shift()
      if (currentMsg) currentMsg.content += token
    }
    if (tokenQueue.length === 0 && streamEnded) {
      clearInterval(typeTimer)
      typeTimer = null
      const cb = finishTypingCallback
      finishTypingCallback = null
      currentMsg = null
      cb && cb()
    }
  }, props.typingSpeed)
}

function enqueueTokens(text, msg) {
  currentMsg = msg
  tokenQueue.push(...tokenize(text))
  startTyping()
}

/** 流式显示时去掉末尾换行符:white-space: pre-wrap 会把末尾 \n 渲染成一个空行,
 *  导致最后一行字与气泡边框之间多出一行(md 渲染阶段 marked 会忽略尾随换行,两态保持一致) */
function displayContent(content) {
  return content.replace(/[\r\n]+$/, '')
}

/* ================================ 工具 ================================ */
/** 滚动到底部:仅用户发送消息 / 打开面板时调用,其他情况不自动沉底 */
function scrollToBottom() {
  nextTick(() => {
    const el = listEl.value
    if (!el) return
    el.scrollTop = el.scrollHeight
  })
}

/** 消息内容 Markdown → 净化后的 HTML(表格/链接/加粗等;DOMPurify 防 XSS) */
function renderMarkdown(text) {
  if (!text) return ''
  // trimEnd:消除消息末尾的换行/空白,避免任何尾随空白影响渲染(与流式阶段 displayContent 保持一致)
  const html = marked.parse(text.trimEnd(), { breaks: true, gfm: true })
  return DOMPurify.sanitize(html)
}

function resetTimeout() {
  clearTimeout(timeoutHandle)
  timeoutHandle = setTimeout(() => {
    // 仅"已有会话且超时"时显示新会话条
    if (messages.value.length > 0 && !showTimeoutBar.value) {
      showTimeoutBar.value = true
    }
  }, props.timeoutMinutes * 60 * 1000)
}

/* ============================ 提示气泡 ============================ */
function startBubble() {
  // 刷新即重新显示:不做任何持久化记录,气泡仅存在于当前页面加载期间
  showBubble.value = true
  startBubbleTimer()
}

function startBubbleTimer() {
  bubbleStart = Date.now()
  bubbleTimer = setTimeout(hideBubble, bubbleRemaining)
}

function pauseBubble() {
  if (!showBubble.value) return
  clearTimeout(bubbleTimer)
  bubbleRemaining -= Date.now() - bubbleStart
}

function resumeBubble() {
  if (!showBubble.value) return
  startBubbleTimer()
}

function hideBubble() {
  clearTimeout(bubbleTimer)
  showBubble.value = false
}

/* ==================== FAB Logo Morph:logo 铰链抬升/放平 ====================
 * 两姿态为同一 logo 的 path 数据,FAB_LOGO_D = 头/脚抬升(= assets/logo.svg 原形状),
 * FAB_FLAT_D = 放平(demo intake-overlay.js 已验证),段数一一对应。
 * 打开面板 t→1 放平、收起 t→0 抬升:520ms easeInOutCubic 逐帧坐标插值改 path d,
 * 无换图标闪烁。 */
const FAB_LOGO_D = "M 113.907 415.486 C 101.061 422.178, 95.286 438.415, 101.533 450.276 C 103.298 453.627, 115.215 467.450, 156.500 514.031 C 163.650 522.098, 177.375 537.628, 187 548.541 C 251.281 621.422, 289.331 663.358, 298.145 671.034 C 323.520 693.133, 345.862 704.809, 374.500 710.940 C 389.405 714.131, 424.515 715.216, 497.217 714.733 C 556.513 714.338, 559.956 714.215, 569 712.168 C 607.133 703.537, 635.682 684.089, 684.098 633.763 C 713.842 602.846, 728.971 587.692, 733.500 584.280 C 745.240 575.435, 753.216 574.354, 776.500 578.452 C 807.313 583.876, 855.639 593.444, 878 598.550 C 889.401 601.152, 920.185 607.992, 929.500 609.991 C 934.450 611.054, 944.794 613.290, 952.487 614.961 C 972.786 619.371, 979.273 618.882, 986.885 612.367 C 1001.340 599.994, 996.214 574.664, 978.242 569.661 C 973.397 568.312, 944.075 561.712, 892.500 550.361 C 836.973 538.141, 784.316 528.170, 763.296 525.896 C 744.443 523.856, 729.315 527.149, 715.103 536.384 C 703.287 544.063, 685.667 560.394, 662.594 585.051 C 613.805 637.193, 591.644 654.354, 562.989 662.181 L 554.500 664.500 478 664.500 C 409.866 664.500, 400.289 664.308, 390.427 662.741 C 375.320 660.342, 369.067 658.596, 359.461 654.095 C 338.941 644.479, 333.819 639.515, 269.512 566.932 C 259.069 555.144, 249.113 543.925, 247.387 542 C 245.661 540.075, 229.300 521.625, 211.030 501 C 130.373 409.948, 138.597 418.798, 131.500 415.408 C 125.198 412.398, 119.789 412.422, 113.907 415.486"
const FAB_FLAT_D = "M 113.9 560 C 101.1 670, 95.3 670, 101.5 670 C 103.3 670, 115.2 670, 156.5 670 C 163.7 670, 177.4 670, 187 670 C 251.3 670, 289.3 670, 298.1 670 C 323.5 670, 345.9 670, 374.5 670 C 389.4 670, 424.5 670, 497.2 670 C 556.5 670, 560 670, 569 670 C 607.1 670, 635.7 670, 684.1 670 C 713.8 670, 729 670, 733.5 670 C 745.2 670, 753.2 670, 776.5 670 C 807.3 670, 855.6 670, 878 670 C 889.4 670, 920.2 670, 929.5 670 C 934.5 670, 944.8 670, 952.5 670 C 972.8 670, 979.3 670, 986.9 670 C 1001.3 560, 996.2 560, 978.2 560 C 973.4 560, 944.1 560, 892.5 560 C 837 560, 784.3 560, 763.3 560 C 744.4 560, 729.3 560, 715.1 560 C 703.3 560, 685.7 560, 662.6 560 C 613.8 560, 591.6 560, 563 560 L 554.5 560 478 560 C 409.9 560, 400.3 560, 390.4 560 C 375.3 560, 369.1 560, 359.5 560 C 338.9 560, 333.8 560, 269.5 560 C 259.1 560, 249.1 560, 247.4 560 C 245.7 560, 229.3 560, 211 560 C 130.4 560, 138.6 560, 131.5 560 C 125.2 560, 119.8 560, 113.9 560"

function parsePathD(str) {
  const out = []
  const re = /([MLC])([^MLC]*)/g
  let m
  while ((m = re.exec(str))) {
    out.push({ c: m[1], n: m[2].trim().split(/[\s,]+/).filter(Boolean).map(Number) })
  }
  return out
}

const FAB_LOGO_SEGS = parsePathD(FAB_LOGO_D)
const FAB_FLAT_SEGS = parsePathD(FAB_FLAT_D)

/** 按 t(0→1) 线性插值两份 path 的坐标并拼回 d */
function mixPathD(a, b, t) {
  let d = ''
  for (let i = 0; i < a.length; i++) {
    const nums = a[i].n.map((v, j) => {
      const x = v + (b[i].n[j] - v) * t
      return Math.round(x * 10) / 10
    })
    if (a[i].c === 'M' || a[i].c === 'L') d += a[i].c + ' ' + nums.join(' ') + ' '
    else {
      d += 'C '
      for (let j = 0; j < nums.length; j += 2) {
        d += nums[j] + ' ' + nums[j + 1] + (j + 2 < nums.length ? ', ' : ' ')
      }
    }
  }
  return d.trim()
}

function easeInOutCubic(x) {
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2
}

const fabMorphD = ref(FAB_LOGO_D) // 模板 :d 绑定;t=0 即抬升姿态
const fabPose = { t: 0, timer: 0 }

function setFabPose(t) {
  fabMorphD.value = mixPathD(FAB_LOGO_SEGS, FAB_FLAT_SEGS, t)
  fabPose.t = t
}

/** openFlag=true 放平(t→1);false 抬升(t→0)。520ms,easeInOutCubic,~60fps */
function animateFabPose(openFlag) {
  const target = openFlag ? 1 : 0
  if (fabPose.timer) clearTimeout(fabPose.timer)
  const from = fabPose.t
  if (Math.abs(from - target) < 0.001) {
    fabPose.timer = 0
    setFabPose(target)
    return
  }
  const t0 = Date.now()
  const dur = 520
  const step = () => {
    const p = Math.min(1, (Date.now() - t0) / dur)
    setFabPose(from + (target - from) * easeInOutCubic(p))
    if (p < 1) fabPose.timer = setTimeout(step, 16)
    else {
      fabPose.timer = 0
      setFabPose(target)
    }
  }
  step()
}

/* 面板开合驱动 morph;immediate 让首帧(收起)直接落到抬升姿态 */
watch(open, (v) => animateFabPose(v), { immediate: true })

/* ============================ 面板开关 ============================ */
function toggleFab() {
  hideBubble()
  if (open.value) {
    closePanel()
  } else {
    openPanel()
  }
}

function openPanel() {
  open.value = true
  scrollToBottom()
}

function closePanel() {
  // 最小化:仅收起面板,不结束会话
  open.value = false
}

/* ==================== 同意时上报当前页面 URL(隐藏消息) ==================== */
/**
 * 用户点 Agree and Continue 时触发(会话由此建立):把当前页面 URL 作为一条隐藏消息发给 Dify。
 * - 不显示 user/assistant 气泡、不触发 loading,失败静默(不打扰用户);
 * - 通过 message_end 拿到 conversation_id 保存,后续对话自动带上 URL 上下文;
 * - 若用户消息已先创建会话(极端竞态),不覆盖已有会话 id;
 * - 每个会话只上报一次:Agree 每会话只会被点一次(consented 置真后门控页即卸载),
 *   无需额外的"已上报"标记;仅靠"在途"守卫跳过重复调用。
 */
function sendPageUrl() {
  if (pageUrlPromise) return // 请求未发完时跳过,避免并发重复上报
  const url = window.location.href
  if (!url) return
  const ctrl = new AbortController()
  const timeoutId = setTimeout(() => ctrl.abort(), props.requestTimeout * 1000)
  pageUrlAborter = ctrl
  pageUrlPromise = (async () => {
    try {
      const res = await fetch(props.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify({
          request_id: genRequestId(),
          query: url,
          response_mode: 'streaming',
          conversation_id: conversationId,
          user: 'ergoHelper',
        }),
        signal: ctrl.signal,
      })
      if (!res.ok) return
      // 消费 SSE 流:拿 message_end 里的 conversation_id;answer 为 666 时丢弃,其余暂存待欢迎语后补播
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let answer = ''
      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop()
        for (const line of lines) {
          const data = line.trim()
          if (!data.startsWith('data:')) continue
          const payload = data.slice(5).trim()
          if (payload === '[DONE]') continue
          let json
          try {
            json = JSON.parse(payload)
          } catch {
            continue
          }
          if ((json.event === 'message' || json.event === 'agent_message') && typeof json.answer === 'string') {
            answer += json.answer
          }
          if (json.event === 'message_end' && json.conversation_id && !conversationId) {
            conversationId = String(json.conversation_id).slice(0, 128)
          }
        }
      }
      // ok666 = 后端明确表示无附加内容,照旧丢弃;其余回复暂存,欢迎语输出完后补播
      const text = answer.trim()
      if (text && text !== 'ok') {
        pendingUrlAnswer = text
        hasUrlAnswer.value = true
      }
    } catch {
      // 静默失败:URL 上下文是增强能力,失败不影响正常对话
    } finally {
      clearTimeout(timeoutId)
      pageUrlPromise = null
      // 注意:这里不清 pageUrlAborter——请求结束与"会话重置"存在极短的重叠窗口,
      // 若在此清空可能把新会话刚建立的中断句柄误清掉;它由 resetSessionState 负责清理
      // 请求完成:若欢迎语已播完,此刻解锁或补播(在途时欢迎语那边不会放行)
      resolveAfterWelcome()
    }
  })()
}

/* ======================= 同意门控 + 首句加载 ======================= */
function agreeAndContinue() {
  consented.value = true
  inputLocked.value = true
  sendBlocked.value = true
  showQuizLink.value = false
  showEndConfirm.value = false
  scrollToBottom()
  // 会话建立:用户同意后才把当前页面 URL 上报给后端,由此拿到 conversation_id
  sendPageUrl()
  // 开局欢迎语:前端写死,不请求后端
  startFirstMessageStream()
}

/* ==================== 前端写死的模拟回复(打字机输出) ==================== */
/** 用打字机逐词输出一段固定文本;meta 可给消息附加标记(如欢迎语 welcome: true) */
function simulateTyping(text, { onDone } = {}, meta = {}) {
  const tokens = tokenize(text.trim())
  if (tokens.length === 0) {
    onDone?.()
    return
  }
  const assistantMsg = reactive({ id: ++msgSeq, role: 'assistant', content: '', streaming: true, ...meta })
  messages.value.push(assistantMsg)
  tokenQueue = tokens
  currentMsg = assistantMsg
  streamEnded = true
  finishTypingCallback = () => {
    assistantMsg.streaming = false
    onDone?.()
  }
  startTyping()
}

/** 开局欢迎语:先显示 Thinking,再逐词输出固定话术 */
function startFirstMessageStream() {
  thinking.value = true
  clearTimeout(simTimer)
  simTimer = setTimeout(() => {
    simTimer = null
    thinking.value = false
    simulateTyping(props.firstMessage, { onDone: unlockAfterWelcome }, { welcome: true })
  }, 600)
}

/* ============================ 发送消息 ============================ */
function sendText(text) {
  const t = (text ?? input.value).trim()
  if (!t || sending.value || inputLocked.value || sendBlocked.value) return
  input.value = ''
  // 若超时条在显示,继续对话并清除
  // 注意:普通手打聊天不摘 quiz 入口——入口仅在完成 Intake 后由 quizFinished 摘除(可上滑回欢迎语下再答)
  showTimeoutBar.value = false
  // 所有消息(含预设快捷主题)一律发给后端作为正常对话
  streamChat(t)
}

function send() {
  sendText(input.value)
}

function onKeydown(e) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    send()
  }
}

/* 输入框自适应高度:最小一行,最多四行(行高随宿主字体动态计算),垂直留白在 wrap 上 */
function autoResize() {
  const el = inputEl.value
  if (!el) return
  el.style.height = 'auto'
  const cs = getComputedStyle(el)
  const line = Math.round(parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.4)
  el.style.height = `${Math.min(Math.max(el.scrollHeight, line), 4 * line)}px`
}

watch(input, autoResize, { flush: 'post' })

/* ======================= Dify SSE 流式对话 ======================= */
async function streamChat(text, opts = {}) {
  // quiz 提交消息已在调用方以标签卡形式入列(skipUserPush),避免重复 push 用户气泡
  if (!opts.skipUserPush) {
    messages.value.push({ id: ++msgSeq, role: 'user', content: text, streaming: false })
  }
  // 仅用户发送消息时沉底
  scrollToBottom()
  thinking.value = true
  sending.value = true
  resetTimeout()

  let assistantMsg = null
  // 收到第一条内容(或需要报错)时才创建流式气泡,同时移除加载态
  // 注意:必须用 reactive 创建,否则通过原始对象修改 content 不会触发 Vue 渲染
  const ensureMsg = () => {
    if (!assistantMsg) {
      assistantMsg = reactive({ id: ++msgSeq, role: 'assistant', content: '', streaming: true })
      messages.value.push(assistantMsg)
      thinking.value = false
    }
    return assistantMsg
  }

  aborter.value = new AbortController()
  let timedOut = false
  // 响应头超时保护:超过 requestTimeout 秒未返回则中断,避免无限挂起
  const timeoutId = setTimeout(() => {
    timedOut = true
    aborter.value?.abort()
  }, props.requestTimeout * 1000)
  try {
    const res = await fetch(props.apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify(
        opts.intake
          ? {
              // 弹窗提交:结构化 intake(answers 为 quiz-definition 的 option id)
              request_id: genRequestId(),
              user: 'ergoHelper',
              conversation_id: conversationId,
              response_mode: 'streaming',
              intake: opts.intake,
            }
          : {
              // 普通聊天:自然语言 query
              request_id: genRequestId(),
              user: 'ergoHelper',
              conversation_id: conversationId,
              response_mode: 'streaming',
              query: text,
            }
      ),
      signal: aborter.value.signal,
    })

    if (!res.ok) {
      ensureMsg().content = GENERIC_ERROR_TEXT
      opts.onFail?.()
      return
    }

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let done = false

    const consumeLine = (raw) => {
      const data = raw.trim()
      if (!data.startsWith('data:')) return
      const payload = data.slice(5).trim()
      if (payload === '[DONE]') return
      let json
      try {
        json = JSON.parse(payload)
      } catch {
        return
      }
      switch (json.event) {
        case 'message':
        case 'agent_message':
          if (json.answer) {
            enqueueTokens(json.answer, ensureMsg())
          }
          break
        case 'message_end':
          if (json.conversation_id) conversationId = String(json.conversation_id).slice(0, 128)
          resetTimeout()
          break
        case 'error':
          ensureMsg().content += (assistantMsg.content ? '\n' : '') + GENERIC_ERROR_TEXT
          break
        default:
          break
      }
    }

    while (!done) {
      const { done: isDone, value } = await reader.read()
      done = isDone
      if (isDone) break
      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop()
      for (const line of lines) consumeLine(line)
    }
    if (buffer.trim()) consumeLine(buffer)
  } catch (err) {
    // 请求失败(网络中断/超时/其他异常):通知调用方(Intake 提交据此回滚完成态与入口)
    opts.onFail?.()
    if (err.name === 'AbortError') {
      if (timedOut) {
        // 30 秒未响应(挂起):固定网络异常话术,输入区保持可用
        tokenQueue = []
        ensureMsg().content = NETWORK_ERROR_TEXT
      } else if (assistantMsg) {
        assistantMsg.content += '\n(已中断)'
      }
    } else {
      // 非超时失败(网络错误/流中断):友好错误话术
      tokenQueue = []
      ensureMsg().content = GENERIC_ERROR_TEXT
    }
  } finally {
    clearTimeout(timeoutId)
    // 流结束:等打字机把队列里的词输出完再收尾
    streamEnded = true
    finishTypingCallback = () => {
      if (assistantMsg) assistantMsg.streaming = false
      thinking.value = false
      sending.value = false
      aborter.value = null
      // 真实回复输出完成后沉底一次(输出期间不跟随)
      scrollToBottom()
    }
    if (tokenQueue.length === 0 && !typeTimer) {
      const cb = finishTypingCallback
      finishTypingCallback = null
      currentMsg = null
      cb()
    } else {
      startTyping()
    }
  }
}

/** 会合点:欢迎语结束且 URL 隐藏请求完成(两者谁后到谁触发)才恢复可发送。
 *  请求仍在途时仅暂缓发送,输入框可打字(请求有 requestTimeout 秒超时兜底,不会永久暂缓):
 *  有暂存回复→与真实对话共用打字机队列补播(期间保持暂缓,避免 token 串扰),播完恢复;
 *  无(666/空/失败)→直接恢复;恢复即解锁发送并展示 quiz 引导行 */
function resolveAfterWelcome() {
  if (!welcomeFinished || pageUrlPromise) return
  if (!pendingUrlAnswer) {
    sendBlocked.value = false
    showQuizLink.value = true
    return
  }
  const text = pendingUrlAnswer
  pendingUrlAnswer = ''
  simulateTyping(text, { onDone: () => {
    sendBlocked.value = false
    showQuizLink.value = true
    scrollToBottom()
  } }, { urlAnswer: true })
  scrollToBottom()
}

function unlockAfterWelcome() {
  scrollToBottom()
  inputLocked.value = false // 欢迎语播完即可打字;能否发送由 sendBlocked 决定
  welcomeFinished = true
  resolveAfterWelcome()
}

/* ============================ Quiz 问卷 ============================ */
/** 由题目 key 与选项 label 反查选项对象(找不到返回 null) */
function quizOptionFor(stepKey, label) {
  const step = QUIZ_STEPS.find((x) => x.key === stepKey)
  return step ? step.options.find((o) => o.label === label) : null
}
/** 反查 chip 短标签(找不到时回退 label) */
function chipFor(stepKey, label) {
  const opt = quizOptionFor(stepKey, label)
  return (opt && opt.chip) || label
}
/** 题干内某选项是否处于选中态(多选看包含、单选看相等) */
function quizOptionActive(step, opt) {
  return step.type === 'multi'
    ? quizAnswers[step.key].includes(opt.label)
    : quizAnswers[step.key] === opt.label
}

/** 组装用户气泡展示的标签卡数据(短 chips;多选视觉更轻 soft:true),末尾附上已选的 Bed size */
function buildQuizCardChips() {
  const chips = []
  quizVisibleSteps.value.forEach((step) => {
    const picked =
      step.type === 'multi' ? quizAnswers[step.key] : quizAnswers[step.key] ? [quizAnswers[step.key]] : []
    picked.forEach((label) => {
      chips.push({ text: chipFor(step.key, label), soft: step.type === 'multi' })
    })
  })
  if (quizSize.value) chips.push({ text: quizSize.value, soft: false })
  return chips
}

/** 单选题:点击即记录;再点同一项则取消选中(清空),与 demo 的单选可反选一致 */
function selectQuizSingle(key, value) {
  quizAnswers[key] = quizAnswers[key] === value ? '' : value
}
/** 多选题:点击切换选中/取消;互斥项(exclusive)选中时清空同题其他,选普通项时摘掉互斥项。
 *  Q1(sleep_change)不再勾「wake tired」时,连带清掉依赖它的条件题 Q2(sleep_measure)旧答案
 *  (对齐 demo:条件题的作答随触发项一起失效,否则会残留一条题面已消失的答案) */
function toggleQuizMulti(key, label, exclusive) {
  const arr = quizAnswers[key]
  if (exclusive) {
    quizAnswers[key] = arr.includes(label) ? [] : [label]
  } else {
    const i = arr.indexOf(label)
    if (i === -1) {
      const ex = QUIZ_STEPS.find((s) => s.key === key).options.find((o) => o.exclusive)
      quizAnswers[key] = [...(ex ? arr.filter((x) => x !== ex.label) : arr), label]
    } else {
      arr.splice(i, 1)
    }
  }
  if (!quizAnswers.sleep_change.includes(QUIZ_TIRED_LABEL)) {
    quizAnswers.sleep_measure = ''
  }
}
/** 题干选项点击入口:按题型分发到单选/多选 */
function onQuizOptionClick(step, opt) {
  if (step.type === 'multi') toggleQuizMulti(step.key, opt.label, !!opt.exclusive)
  else selectQuizSingle(step.key, opt.label)
}
/** 下一步(当前题未作答时禁用) */
function quizNext() {
  if (!quizStepValid.value) return
  if (quizStep.value < quizVisibleSteps.value.length - 1) quizStep.value++
}
/** ←/→ 自由浏览:不要求当前题已答;边界禁用由模板控制 */
function browseQuiz(delta) {
  const next = quizStep.value + delta
  if (next >= 0 && next < quizVisibleSteps.value.length) quizStep.value = next
}
/** 跳到第一道未作答的题(对应 demo 的 Review) */
function reviewMissing() {
  const i = quizVisibleSteps.value.findIndex((s) => !quizStepPicked(s))
  if (i >= 0) quizStep.value = i
}
/** 关闭抽屉:不重置已选选项(下次打开保留进度) */
function closeQuiz() {
  showQuizDrawer.value = false
}
/** 提交(末题主按钮):满足提交门槛即可(partialSubmit 下部分作答亦可)。先以标签卡消息入列,
 *  再把结构化的 intake 走 chat-messages 发给后端(不再整合成一段自然语言);发送成功才置
 *  quizFinished 摘掉入口(本会话不再出现)。发送失败时回滚:撤掉这条标签卡并恢复入口 */
function submitQuiz() {
  if (!quizCanSubmit.value || sending.value || inputLocked.value) return
  const chips = buildQuizCardChips()
  quizFinished.value = true
  closeQuiz()
  // 用户气泡只展示标签卡(chips);不再有自然语言正文,content 留空
  const card = { id: ++msgSeq, role: 'user', content: '', streaming: false, quizCard: true, chips }
  messages.value.push(card)
  scrollToBottom()
  // 复用现有 SSE 发送链路(thinking/打字机/错误处理);skipUserPush 避免重复 push 用户气泡
  streamChat('', {
    skipUserPush: true,
    intake: buildIntakePayload(),
    // 请求层失败(网络中断/超时/非 2xx)时回滚:入口重新出现,用户可重新提交
    onFail: () => {
      const i = messages.value.findIndex((m) => m.id === card.id)
      if (i !== -1) messages.value.splice(i, 1)
      quizFinished.value = false
      scrollToBottom()
    },
  })
  // 兼容期:结构化 intake 之外,仍并行调用 v1/intake-answers 上报
  sendIntakeAnswers()
}

/* ---- 末题附加的 Bed size 单选器(对齐 demo bridge 注入的 size picker) ---- */
const sizeTrackEl = ref(null)
const sizeAtStart = ref(true) // 轨道已在最左端(‹ 置灰)
const sizeAtEnd = ref(false) // 轨道已在最右端(› 置灰)
/** 选中/取消 Bed size(空串表示 Not sure yet);选中后把该项滚到轨道可视区中间 */
function pickQuizSize(value) {
  quizSize.value = value
  revealSize(value)
  syncSizeArrows()
}
/** 「‹ / ›」箭头:左右横滑尺寸轨道 */
function scrollSizeTrack(direction) {
  const el = sizeTrackEl.value
  if (!el) return
  el.scrollBy({ left: direction * Math.max(100, el.clientWidth * 0.75), behavior: 'smooth' })
}
/** 重算两端箭头禁用态:轨道滚到顶/底时对应箭头置灰(对齐 demo 的 syncArrows) */
function syncSizeArrows() {
  const el = sizeTrackEl.value
  if (!el) return
  sizeAtStart.value = el.scrollLeft <= 1
  sizeAtEnd.value = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
}
/** 把选中/聚焦的尺寸 chip 滚到轨道中间,避免选中项停在视口外(对齐 demo 的 reveal) */
function revealSize(value) {
  const el = sizeTrackEl.value
  if (!el) return
  const chip = [...el.children].find((c) => (c.dataset.size || '') === value)
  if (!chip) return
  el.scrollTo({ left: Math.max(0, chip.offsetLeft - (el.clientWidth - chip.offsetWidth) / 2), behavior: 'smooth' })
}

/* ---- 抽屉渲染用的派生文案 ---- */
/** 当前可见题对象(越界时为 null,模板用 v-if 兜底) */
const quizCurrentStep = computed(() => quizVisibleSteps.value[quizStep.value] || null)
/** 选择计数文案:N selected / No selection yet(对齐 demo 的 ei-selection-count) */
function quizCountLabel(step) {
  const v = quizAnswers[step.key]
  const n = step.type === 'multi' ? v.length : v ? 1 : 0
  return n ? `${n} selected` : 'No selection yet'
}
/** 末题摘要标题:Ready for your match / N unanswered · Initial fit available / Choose at least one answer */
const quizSummaryTitle = computed(() => {
  if (quizAllDone.value) return 'Ready for your match'
  if (quizAnsweredCount.value > 0) {
    return `${quizVisibleSteps.value.length - quizAnsweredCount.value} unanswered · Initial fit available`
  }
  return 'Choose at least one answer'
})
/** Bed size 轨道在「切到末题」或「打开抽屉」后才进 DOM,此时重算两端箭头禁用态 */
watch([quizCurrentStep, showQuizDrawer], () => nextTick(syncSizeArrows))

/* ==================== Intake 答案上报(v1/intake-answers) ==================== */

/** 由题 key 与选项 label 反查 quiz-definition 的稳定 option id(上报后端用;找不到时回退 label) */
function optionIdFor(stepKey, label) {
  const opt = quizOptionFor(stepKey, label)
  return (opt && opt.id) || label
}

/** 结构化作答:各题答案一律映射成 quiz-definition 的 option id(多选数组、单选字符串或 null) */
function buildQuizAnswers() {
  return {
    q1: quizAnswers.sleep_change.map((label) => optionIdFor('sleep_change', label)),
    q1b: quizAnswers.sleep_measure ? optionIdFor('sleep_measure', quizAnswers.sleep_measure) : null,
    q2: quizAnswers.how_far ? optionIdFor('how_far', quizAnswers.how_far) : null,
    q3: quizAnswers.room_access.map((label) => optionIdFor('room_access', label)),
  }
}

/** 弹窗提交用的结构化 intake(走 chat-messages 的 intake 参数,不再整合成一段自然语言):
 *  quiz_version 固定上报;size 未选传 null */
function buildIntakePayload() {
  return {
    type: 'intake',
    quiz_version: QUIZ_VERSION,
    answers: buildQuizAnswers(),
    size: quizSize.value || null,
  }
}

/** 上报 intake 答案(独立接口 /api/v1/intake-answers):
 *  quiz 提交(末题主按钮)时,与 chat-messages 的 intake 并行发出(兼容期两条都发);
 *  会话 id 用首次进入页面时从 chat-messages 流中保存的 conversationId;
 *  未作答:多选传 []、单选传 null、Bed size 未选传 null */
function sendIntakeAnswers() {
  fetch(props.intakeApiUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      conversation_id: conversationId,
      user: 'ergoHelper',
      ...buildQuizAnswers(),
      size: quizSize.value || null,
    }),
  })
    .then((res) => {
      // TODO: 响应处理待定,暂仅消费返回体
      return res.json().catch(() => null)
    })
    .catch(() => {
      // 静默失败:上报失败不影响正常对话
    })
}

/* ======================= End Chat 确认层 ======================= */
function requestEndChat() {
  showEndConfirm.value = true
}

function cancelEndChat() {
  showEndConfirm.value = false
}

/** 会话重置(End Chat 与超时新会话共用):中断在途请求/打字机/延迟回复,并清空整段会话与同意状态。
 *  差异部分(面板开合、输入暂缓、确认层开关)由调用方各自补,避免两份 30 行的重复清单 */
function resetSessionState() {
  // 中断进行中的流、打字机与延迟模拟回复
  aborter.value?.abort()
  clearInterval(typeTimer)
  clearTimeout(simTimer)
  typeTimer = null
  tokenQueue = []
  currentMsg = null
  finishTypingCallback = null
  // 中断在途的 URL 上报请求:否则新会话点 Agree 时会被"在途"守卫挡住而漏报,
  // 旧请求的 message_end 还可能把旧 conversation_id 回填进新会话
  pageUrlAborter?.abort()
  pageUrlAborter = null
  pageUrlPromise = null
  // 清空会话与同意状态:重新走门控,由下次点 Agree 重新上报页面 URL
  messages.value = []
  conversationId = ''
  consented.value = false
  inputLocked.value = true
  showQuizLink.value = false
  showQuizDrawer.value = false
  quizFinished.value = false
  quizStep.value = 0
  Object.assign(quizAnswers, { sleep_change: [], sleep_measure: '', how_far: '', room_access: [] })
  quizSize.value = ''
  thinking.value = false
  sending.value = false
  showTimeoutBar.value = false
  input.value = ''
  pendingUrlAnswer = ''
  welcomeFinished = false
  hasUrlAnswer.value = false
  clearTimeout(timeoutHandle)
}

/** End Chat:收起面板,并保持输入暂缓(下次点 FAB 开启全新对话) */
function confirmEndChat() {
  open.value = false
  resetSessionState()
  sendBlocked.value = true
  showEndConfirm.value = false
}

/* ======================= 超时新会话(重置会话与同意状态,等同刷新) ======================= */
/** 超时新会话:面板保持打开,清空后重新走门控 */
function startNewChat() {
  resetSessionState()
}

/* ================================ 生命周期 ================================ */
function onGlobalKeydown(e) {
  // Escape 关闭面板()
  if (e.key === 'Escape' && open.value) closePanel()
}

onMounted(() => {
  startBubble()
  window.addEventListener('keydown', onGlobalKeydown)
})
onBeforeUnmount(() => {
  clearTimeout(bubbleTimer)
  clearTimeout(timeoutHandle)
  clearTimeout(simTimer)
  clearTimeout(fabPose.timer)
  clearInterval(typeTimer)
  aborter.value?.abort()
  window.removeEventListener('keydown', onGlobalKeydown)
})
</script>

<template>
  <!-- Teleport 到 body:组件 DOM 挂在 body 下,position: fixed 悬浮不受宿主容器 transform/filter 影响 -->
  <Teleport to="body">
    <div class="ergo-advisor" :style="{ '--ew-accent': accent }">
    <!-- 提示气泡 -->
    <transition name="ergo-advisor-bubble">
      <div
        v-if="showBubble"
        class="ergo-advisor__prompt"
        @mouseenter="pauseBubble"
        @mouseleave="resumeBubble"
      >
        <span class="ergo-advisor__prompt-text">{{ bubbleText }}</span>
        <button class="ergo-advisor__prompt-close" type="button" aria-label="关闭提示" @click="hideBubble">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </transition>



    <!-- FAB 浮动入口:始终品牌 logo(不再切箭头/图标)。打开面板时 logo 铰链「放平」,
         收起回到头/脚抬升姿态——SVG path 坐标插值形变(demo FAB morph,见 PRD-FAB-Logo-Morph)。
         颜色跟随 currentColor:收起=白(蓝底),打开=品牌蓝(白底) -->
    <button
      class="ergo-advisor__fab"
      :class="{ 'ergo-advisor__fab--open': open }"
      type="button"
      :aria-label="open ? '收起' : '打开聊天'"
      @click="toggleFab"
    >
      <svg class="ergo-advisor__fab-icon" viewBox="0 0 1066 1066" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path class="ergo-advisor__fab-logo-d" :d="fabMorphD" fill="currentColor" fill-rule="evenodd" />
      </svg>
    </button>




    <!-- 聊天面板 -->
    <transition name="ergo-advisor-panel">
      <div v-if="open" class="ergo-advisor__panel" role="dialog" aria-label="在线顾问">
        <!-- 顶栏:标题 + 最小化 + 关闭 -->
        <header class="ergo-advisor__header">
          <div class="ergo-advisor__header-left">
            <img class="ergo-advisor__header-logo" :src="logoUrl" alt="logo" />
            <span class="ergo-advisor__header-title">{{ title }}</span>
          </div>
          <div class="ergo-advisor__header-actions">
            <button class="ergo-advisor__header-btn" type="button" aria-label="最小化" @click="closePanel">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
            <button class="ergo-advisor__header-btn" type="button" aria-label="结束对话" @click="requestEndChat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </header>

        <!-- 同意门控:固定头部 + 下半部分(免责声明 + 按钮) -->
        <div v-if="!consented" class="ergo-advisor__gate">
          <div class="ergo-advisor__hero">
            <div class="ergo-advisor__consent-icon" aria-hidden="true">
              <img class="ergo-advisor__consent-logo" :src="logoUrl" alt="logo" />
            </div>
            <div class="ergo-advisor__consent-title">{{ advisorName }}</div>
            <div class="ergo-advisor__consent-tagline">{{ advisorTagline }}</div>
          </div>
          <div class="ergo-advisor__consent">
            <p class="ergo-advisor__disclaimer">{{ disclaimer }}</p>
            <p class="ergo-advisor__disclaimer ergo-advisor__disclaimer--ai">{{ aiDisclaimer }}</p>
            <button class="ergo-advisor__agree" type="button" @click="agreeAndContinue">Agree and Continue</button>
          </div>
        </div>

        <!-- 对话主体:hero + today 与消息一起滚动 -->
        <template v-else>
          <div
            ref="listEl"
            class="ergo-advisor__messages"
          >
            <!-- 头部(随对话一起滚动,保持在内容顶部) -->
            <div class="ergo-advisor__hero">
              <div class="ergo-advisor__consent-icon" aria-hidden="true">
                <img class="ergo-advisor__consent-logo" :src="logoUrl" alt="logo" />
              </div>
              <div class="ergo-advisor__consent-title">{{ advisorName }}</div>
              <div class="ergo-advisor__consent-tagline">{{ advisorTagline }}</div>
            </div>
            <!-- today 分隔字段(仅展示,无实际意义) -->
            <div class="ergo-advisor__today">Today</div>

            <!-- 消息线程:内部贴底(最后一条消息紧贴输入区),hero/today 保持顶部 -->
            <div class="ergo-advisor__thread">
            <template v-for="msg in messages" :key="msg.id">
            <div
              class="ergo-advisor__msg"
              :class="`ergo-advisor__msg--${msg.role}`"
            >
              <!-- AI 回复带头像(logo,无边框无阴影,底部对齐);欢迎语在有后端补播内容时只留占位不显示 logo -->
              <div
                v-if="msg.role === 'assistant'"
                class="ergo-advisor__avatar"
                :class="{ 'ergo-advisor__avatar--ghost': msg.welcome && hasUrlAnswer }"
              >
                <img :src="logoUrl" alt="" />
              </div>
              <div class="ergo-advisor__bubble" :class="{ 'ergo-advisor__bubble--card': msg.quizCard }">
                  <!-- 打字机期间显示纯文本,完成后渲染为富文本(表格/链接等) -->
                  <template v-if="msg.streaming">
                    <span>{{ displayContent(msg.content) }}</span>
                    <span class="ergo-advisor__cursor" aria-hidden="true"></span>
                  </template>
                  <!-- Intake 提交消息:用户气泡显示简洁标签卡(后端仍收到 content 中的完整模板) -->
                  <div v-else-if="msg.quizCard" class="ergo-advisor__quiz-card">
                    <div class="ergo-advisor__quiz-card-top">
                      <span class="ergo-advisor__quiz-card-kicker">Quick match</span>
                      <span class="ergo-advisor__quiz-card-sub">Your picks</span>
                    </div>
                    <div class="ergo-advisor__quiz-card-chips">
                      <span
                        v-for="(c, ci) in msg.chips"
                        :key="ci"
                        class="ergo-advisor__quiz-chip"
                        :class="{ 'is-soft': c.soft }"
                      >{{ c.text }}</span>
                    </div>
                  </div>
                  <div v-else class="ergo-advisor__md" v-html="renderMarkdown(msg.content)"></div>
                </div>
            </div>

            <!-- quiz 引导行:URL 补播消息或欢迎语下方(锚定不随对话下沉;完成 Intake 后摘除)
                 两行软入口:主链接 + 副提示(对齐 demo 的 ei-soft-entry) -->
            <div
              v-if="showQuizLink && !quizFinished && !showTimeoutBar && msg.id === quizAnchorId"
              class="ergo-advisor__quiz-entry"
            >
              <button
                class="ergo-advisor__quiz-link"
                type="button"
                @click="showQuizDrawer = true"
              >
                Three quick taps and I'll show you two or three beds.
              </button>
              <span class="ergo-advisor__quiz-link-hint">Or just type what you're after and skip ahead.</span>
            </div>
            </template>

            <!-- Thinking 加载态:三个品牌蓝跳动圆点 + Thinking... -->
            <div v-if="thinking" class="ergo-advisor__msg ergo-advisor__msg--assistant">
              <div class="ergo-advisor__avatar">
                <img :src="logoUrl" alt="" />
              </div>
              <div class="ergo-advisor__thinking" aria-label="正在思考">
                <span class="ergo-advisor__dot"></span>
                <span class="ergo-advisor__dot"></span>
                <span class="ergo-advisor__dot"></span>
                <span class="ergo-advisor__thinking-text">Thinking...</span>
              </div>
            </div>
            </div>
          </div>

          <!-- 底部:超时后显示新会话条(替换输入区,),否则显示输入区 -->
          <div v-if="showTimeoutBar" class="ergo-advisor__timeout">
            <p class="ergo-advisor__timeout-text">
              Your session has expired. Refresh or start a new chat to continue.
            </p>
            <button class="ergo-advisor__timeout-btn" type="button" @click="startNewChat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                <path d="M12 8v6M9 11h6" />
              </svg>
              Start a new chat
            </button>
          </div>
          <!-- 输入区 -->
          <footer v-else class="ergo-advisor__inputbar">
            <div class="ergo-advisor__inputrow">
              <div class="ergo-advisor__input-wrap">
                <textarea
                  ref="inputEl"
                  v-model="input"
                  class="ergo-advisor__input"
                  :placeholder="inputLocked ? lockedPlaceholder : placeholder"
                  :disabled="inputLocked"
                  rows="1"
                  @keydown="onKeydown"
                ></textarea>
                <button
                  v-if="input.trim()"
                  class="ergo-advisor__send"
                  type="button"
                  :disabled="sending || sendBlocked"
                  aria-label="Send"
                  @click="send"
                >
                  <!-- :单一描边纸飞机 path -->
                  <svg class="ergo-advisor__send-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                  </svg>
                </button>
              </div>
            </div>
            <p v-if="!inputLocked" class="ergo-advisor__input-disclaimer">{{ aiDisclaimer }}</p>
          </footer>
        </template>

        <!-- End Chat 确认层 -->
        <transition name="ergo-advisor-fade">
          <div v-if="showEndConfirm" class="ergo-advisor__confirm-overlay">
            <div class="ergo-advisor__confirm" role="alertdialog" aria-label="结束对话确认">
              <button class="ergo-advisor__confirm-danger" type="button" @click="confirmEndChat">End Chat</button>
              <button class="ergo-advisor__confirm-ghost" type="button" @click="cancelEndChat">Cancel</button>
            </div>
          </div>
        </transition>

        <!-- Quiz 底部抽屉:逐题渲染,题干/选项/题数随可见题序列(条件题)变化 -->
        <transition name="ergo-advisor-drawer">
          <div v-if="showQuizDrawer" class="ergo-advisor__quiz-overlay" @click.self="closeQuiz">
            <div class="ergo-advisor__quiz-drawer" role="dialog" aria-modal="true" aria-label="Bed quiz">
              <div class="ergo-advisor__quiz-grabber" aria-hidden="true"></div>
              <!-- 顶部:Quick match kicker + 关闭 -->
              <div class="ergo-advisor__quiz-head">
                <span class="ergo-advisor__quiz-kicker">Quick match</span>
                <button class="ergo-advisor__quiz-close" type="button" aria-label="Close quiz" @click="closeQuiz">×</button>
              </div>
              <!-- 进度:圆点(当前 on 蓝 / 已答 done) + Question X of N(N 随条件题增减) -->
              <div class="ergo-advisor__quiz-progress">
                <div class="ergo-advisor__quiz-dots">
                  <span
                    v-for="(step, i) in quizVisibleSteps"
                    :key="step.key"
                    class="ergo-advisor__quiz-dot"
                    :class="{ on: i === quizStep, done: i !== quizStep && quizStepPicked(step) }"
                  ></span>
                </div>
                <span class="ergo-advisor__quiz-progress-label">
                  Question {{ quizStep + 1 }} of {{ quizVisibleSteps.length }}
                </span>
              </div>
              <!-- 滚动区:题干 + 选项 + 选择计数 + 提示 + 末题的 Bed size 与未答摘要 -->
              <div
                class="ergo-advisor__quiz-body"
                :class="{ 'is-flush-bottom': quizCurrentStep?.key === 'how_far' }"
              >
                <template v-if="quizCurrentStep">
                  <h3 class="ergo-advisor__quiz-title">{{ quizCurrentStep.title }}</h3>
                  <p v-if="quizCurrentStep.hint" class="ergo-advisor__quiz-hint">{{ quizCurrentStep.hint }}</p>
                  <p class="ergo-advisor__quiz-count" aria-live="polite">{{ quizCountLabel(quizCurrentStep) }}</p>
                  <div
                    class="ergo-advisor__quiz-options"
                    :class="{ 'is-pair': quizCurrentStep.key === 'sleep_change' }"
                  >
                    <button
                      v-for="opt in quizCurrentStep.options"
                      :key="opt.label"
                      class="ergo-advisor__quiz-option"
                      :class="{ 'is-active': quizOptionActive(quizCurrentStep, opt) }"
                      type="button"
                      :aria-pressed="String(quizOptionActive(quizCurrentStep, opt))"
                      @click="onQuizOptionClick(quizCurrentStep, opt)"
                    >
                      <span
                        class="ergo-advisor__quiz-option-dot"
                        :class="quizCurrentStep.type === 'single' ? 'is-radio' : 'is-check'"
                      ></span>
                      <span class="ergo-advisor__quiz-option-text">{{ opt.label }}<span v-if="opt.recommended" class="ergo-advisor__quiz-rec">Recommended</span><span v-if="opt.desc" class="ergo-advisor__quiz-option-desc">{{ opt.desc }}</span></span>
                    </button>
                  </div>

                  <!-- 末题附加:Bed size(可选) —— 标题右侧一对箭头,控制下方一行选项横向滚动 -->
                  <div
                    v-if="quizCurrentStep.key === 'room_access'"
                    class="ergo-advisor__quiz-size"
                    role="group"
                    aria-label="Bed size"
                  >
                    <div class="ergo-advisor__quiz-size-head">
                      <span class="ergo-advisor__quiz-size-legend">
                        Bed size<span class="ergo-advisor__quiz-size-optional">Optional</span>
                      </span>
                      <div class="ergo-advisor__quiz-size-nav">
                        <button
                          class="ergo-advisor__quiz-size-arrow"
                          type="button"
                          aria-label="Previous sizes"
                          :disabled="sizeAtStart"
                          @click="scrollSizeTrack(-1)"
                        >‹</button>
                        <button
                          class="ergo-advisor__quiz-size-arrow"
                          type="button"
                          aria-label="More sizes"
                          :disabled="sizeAtEnd"
                          @click="scrollSizeTrack(1)"
                        >›</button>
                      </div>
                    </div>
                    <p class="ergo-advisor__quiz-size-hint">Use the arrows or swipe to see all sizes.</p>
                    <div
                      ref="sizeTrackEl"
                      class="ergo-advisor__quiz-size-track"
                      aria-label="Bed sizes"
                      @scroll="syncSizeArrows"
                    >
                      <button
                        class="ergo-advisor__quiz-size-chip"
                        :class="{ 'is-active': quizSize === '' }"
                        type="button"
                        data-size=""
                        :aria-pressed="String(quizSize === '')"
                        @click="pickQuizSize('')"
                      >Not sure yet</button>
                      <button
                        v-for="size in QUIZ_SIZE_OPTIONS"
                        :key="size"
                        class="ergo-advisor__quiz-size-chip"
                        :class="{ 'is-active': quizSize === size }"
                        type="button"
                        :data-size="size"
                        :aria-pressed="String(quizSize === size)"
                        @click="pickQuizSize(quizSize === size ? '' : size)"
                      >{{ size }}</button>
                    </div>
                  </div>

                  <!-- 未答摘要(允许部分提交且位于末题时):N unanswered · Initial fit available + Review 跳缺失题 -->
                  <div
                    v-if="quizIsLastStep && partialSubmit"
                    class="ergo-advisor__quiz-summary"
                    :class="{ 'is-complete': quizAllDone }"
                    aria-live="polite"
                  >
                    <strong>{{ quizSummaryTitle }}</strong>
                    <button
                      v-if="!quizAllDone"
                      class="ergo-advisor__quiz-summary-review"
                      type="button"
                      @click="reviewMissing"
                    >Review</button>
                  </div>
                </template>
              </div>
              <!-- 底部:左侧 Back to chat + 右侧 ← / → 自由浏览 + 主按钮(Continue / See my match / See initial fit) -->
              <div class="ergo-advisor__quiz-footer">
                <button class="ergo-advisor__quiz-skip" type="button" @click="closeQuiz">Back to chat</button>
                <div class="ergo-advisor__quiz-nav">
                  <button
                    class="ergo-advisor__quiz-navbtn"
                    type="button"
                    aria-label="Previous question"
                    :disabled="quizStep === 0"
                    @click="browseQuiz(-1)"
                  >←</button>
                  <button
                    class="ergo-advisor__quiz-navbtn"
                    type="button"
                    aria-label="Next question"
                    :disabled="quizIsLastStep"
                    @click="browseQuiz(1)"
                  >→</button>
                  <button
                    class="ergo-advisor__quiz-primary"
                    type="button"
                    :disabled="quizIsLastStep ? !quizCanSubmit : !quizStepValid"
                    @click="quizIsLastStep ? submitQuiz() : quizNext()"
                  >{{ quizPrimaryLabel }}</button>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>
    </div>
  </Teleport>
</template>

<style scoped>
.ergo-advisor {
  /* ===== 品牌色阶===== */
  --ew-100: #e5e5e5; /* 悬浮背景、提示背景/边框 */
  --ew-200: #e5e5e5; /* 浅边框、分割线 */
  --ew-300: #dde8ec; /* 链接卡片边框 */
  --ew-400: #8a8a8a; /* 次要强调、弱化文字 */
  --ew-500: #5b9eb9; /* 基准主色:标准按钮、用户气泡、高亮 */
  --ew-600: #4a8ca5; /* hover 态、激活态 */
  --ew-700: #222; /* 主文字色 */
  --ew-800: #333; /* 次要文字 */
  --ew-900: #1a1a1a; /* 最深色、高对比文字 */

  --ew-accent: var(--ew-500);
  --ew-accent-hover: var(--ew-600);
  --ew-text: var(--ew-700);

  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 2147483000;
  /* UCity Pro 由官网宿主页面提供(不打包);缺失时静默回退到系统字体,不会报错 */
  font-family: 'UCity Pro', 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', system-ui, sans-serif;
  /* 按 x-height 归一:上面这条回退链在各平台会命中不同字体(Segoe UI / PingFang SC / Roboto),
   * 相同 font-size 下视觉大小能差 6% 以上。0.5 ≈ Segoe UI 的 x-height/em,
   * 浏览器会缩放其余字体向它对齐;不支持的浏览器直接忽略该属性,无副作用 */
  font-size-adjust: 0.5;
  color: var(--ew-text);
  /* 触摸设备:去掉点击时系统加的灰色高亮块 */
  -webkit-tap-highlight-color: transparent;
}

/* 表单控件不继承 .ergo-advisor 的字体:UA 样式表给 button/input/textarea/select 设了
 * font: -webkit-small-control(Chrome,= 浏览器默认字号 -2pt)/ -moz-button(Firefox,取自系统设置),
 * 该 shorthand 同时把 font-size-adjust 重置为 none,所以两者都要显式拉回继承。
 * 注意不含 font-size:各控件都自带 px 字号,保持各自的值 */
.ergo-advisor button,
.ergo-advisor input,
.ergo-advisor textarea,
.ergo-advisor select {
  font-family: inherit;
  font-size-adjust: inherit;
}

/* ============================ FAB ============================ */
/* 收起:品牌蓝底 + 白 logo(brightness(0) invert 滤镜,);展开:白底 + 蓝箭头 + 描边 */
.ergo-advisor__fab {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border: none;
  border-radius: 50%;
  background: var(--ew-accent);
  color: #fff;
  cursor: pointer;
  box-shadow: 0 6px 20px rgb(91 158 185 / 35%);
  transition: background-color 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
}

.ergo-advisor__fab:hover {
  transform: scale(1.05);
}

.ergo-advisor__fab:active {
  transform: scale(0.95);
}

/* 展开状态:白底 + 蓝箭头 + 浅灰描边 */
.ergo-advisor__fab--open {
  background: #fff;
  color: var(--ew-accent);
  outline: 1px solid #d0d0d0;
}

.ergo-advisor__fab-icon {
  width: 32px;
  height: 32px;
}

.ergo-advisor__header-logo {
  object-fit: contain;
}

/* FAB 内 logo path:fill 跟随 currentColor(收起=白、打开=品牌蓝),与背景过渡同拍 */
.ergo-advisor__fab-logo-d {
  transition: fill 0.25s ease;
}

@media (max-width: 768px) {
  .ergo-advisor__fab {
    width: 52px;
    height: 52px;
  }
  .ergo-advisor__fab-icon {
    /* 移动端与 React(h-7 = 28px)一致 */
    width: 28px;
    height: 28px;
  }
}

/* ============================ 提示气泡 ============================ */
/* 注意:独立 class(ergo-advisor__prompt),不能与消息气泡共用 .ergo-advisor__bubble,
   否则会被消息气泡的 max-width:82% 规则(特异性相同且在后)覆盖,压成竖向窄条 */
.ergo-advisor__prompt {
  position: absolute;
  right: 0;
  bottom: calc(100% + 14px);
  display: flex;
  align-items: flex-start;
  gap: 6px;
  width: 280px;
  max-width: calc(100vw - 48px);
  padding: 12px 16px;
  border: 1px solid #e5e5e5;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 8px 24px rgb(0 0 0 / 12%);
  font-size: 14px;
  line-height: 1.45;
  color: #333;
}

.ergo-advisor__prompt-text {
  flex: 1;
}

.ergo-advisor__prompt-close {
  flex: none;
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #f1f1f1;
  color: #999;
  cursor: pointer;
  /* 默认隐藏,悬停气泡时显示 */
  opacity: 0;
  transition: opacity 0.15s ease;
}

.ergo-advisor__prompt:hover .ergo-advisor__prompt-close,
.ergo-advisor__prompt-close:focus-visible {
  opacity: 1;
}

.ergo-advisor__prompt-close:hover {
  color: #555;
}

.ergo-advisor__prompt-close svg {
  width: 12px;
  height: 12px;
}

/* ============================ 面板 ============================ */
.ergo-advisor__panel {
  position: absolute;
  right: 0;
  bottom: calc(100% + 12px);
  display: flex;
  flex-direction: column;
  width: 370px;
  max-width: calc(100vw - 32px);
  height: 600px;
  max-height: calc(100vh - 112px);
  overflow: hidden;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 12px 40px rgb(0 0 0 / 18%);
}

/* 顶栏 */
.ergo-advisor__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: var(--ew-accent);
  color: #fff;
}

.ergo-advisor__header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.ergo-advisor__header-logo {
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #fff;
  padding: 4px;
  box-sizing: border-box;
}

.ergo-advisor__header-title {
  font-size: 15px;
  font-weight: 600;
}

.ergo-advisor__header-actions {
  display: flex;
  gap: 2px;
}

.ergo-advisor__header-btn {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #fff;
  cursor: pointer;
}

.ergo-advisor__header-btn:hover {
  background: rgb(255 255 255 / 15%);
}

.ergo-advisor__header-btn svg {
  width: 18px;
  height: 18px;
}

/* 共用头部(门控与对话):图标 + 标题 + 副标题 */
.ergo-advisor__hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 24px 8px 8px;
  text-align: center;
}

.ergo-advisor__consent-icon {
  display: grid;
  place-items: center;
  width: 56px;
  height: 56px;
  margin-bottom: 12px;
  border: 1px solid #e8e8e8;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 2px 8px rgb(0 0 0 / 6%);
}

.ergo-advisor__consent-logo {
  width: 80%;
  height: 80%;
  border-radius: 50%;
}

.ergo-advisor__consent-title {
  font-size: 17px;
  font-weight: 700;
  color: #1a1a1a;
}

.ergo-advisor__consent-tagline {
  font-size: 13px;
  color: #8a8a8a;
  line-height: 1.4;
}

/* 同意门控:固定头部 + 下半部分(免责声明 + 按钮,靠底部) */
.ergo-advisor__gate {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.ergo-advisor__consent {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  padding: 8px 24px 20px;
  text-align: center;
}

.ergo-advisor__disclaimer {
  margin: 0;
  font-size: 11px;
  line-height: 1.6;
  color: #8a8a8a;
}

.ergo-advisor__disclaimer--ai {
  color: #b0b0b0;
}

.ergo-advisor__agree {
  margin-top: 8px;
  padding: 12px 22px;
  border: none;
  border-radius: 999px;
  background: var(--ew-accent);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.2s ease;
}

.ergo-advisor__agree:hover {
  background: var(--ew-accent-hover);
}

/* today 分隔字段(仅展示) */
.ergo-advisor__today {
  margin: 8px 0 16px;
  font-size: 12px;
  font-weight: 500;
  color: #b0b0b0;
  text-align: center;
  letter-spacing: 0.5px;
}

/* 消息区:hero/today 在顶部,thread 内部贴底 */
.ergo-advisor__messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 14px 14px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  scroll-behavior: smooth;
}

/* 消息线程:内容多时撑高由 messages 滚动,无贴底需求(面板高度自适应) */
.ergo-advisor__thread {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ergo-advisor__messages::-webkit-scrollbar {
  width: 6px;
}

.ergo-advisor__msg {
  display: flex;
  gap: 8px;
}
.ergo-advisor__msg--user {
  /* 右对齐 + 左 padding 28px(头像 20px + gap 8px),气泡撑满 28px→右缘:左缘与 AI 气泡对齐且右缘贴右 */
  justify-content: flex-end;
  padding-left: 28px;
}
.ergo-advisor__msg--assistant {
  justify-content: flex-start;
  /* 头像贴气泡底部 */
  align-items: flex-end;
}

/* AI 头像:logo 图,无边框无阴影 */
.ergo-advisor__avatar {
  flex: none;
  width: 20px;
  height: 20px;
  margin-bottom: 2px;
  border-radius: 50%;
  overflow: hidden;
}

.ergo-advisor__avatar img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 占位头像:盒子与间距照旧(气泡位置不动),仅 logo 不可见 */
.ergo-advisor__avatar--ghost {
  visibility: hidden;
}

.ergo-advisor__bubble {
  max-width: min(88%, 320px);
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 14px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: normal;
  overflow-wrap: anywhere;
}

.ergo-advisor__msg--user .ergo-advisor__bubble {
  max-width: 100%;
  background: var(--ew-accent);
  color: #fff;
  border-bottom-right-radius: 6px;
}

.ergo-advisor__msg--user .ergo-advisor__bubble span,
.ergo-advisor__msg--user .ergo-advisor__bubble .ergo-advisor__md,
.ergo-advisor__msg--user .ergo-advisor__bubble .ergo-advisor__md :deep(*) {
  color: #fff;
}

/* Intake 提交消息:用户气泡 → 白底标签卡(Quick match / Your picks + chips)
   动态 class(ergo-advisor__bubble--card) 标记,避免 scoped 下 :has() 的 scope 改写问题;
   color 用 !important 压过上面 .msg--user .bubble span 的白色文字规则 */
.ergo-advisor__msg--user .ergo-advisor__bubble--card {
  background: #fff !important;
  color: var(--ew-700);
  border: 1px solid var(--ew-300);
  border-bottom-right-radius: 6px;
  padding: 12px 14px;
  width: 100%;
  box-sizing: border-box;
}
.ergo-advisor__quiz-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
}
.ergo-advisor__quiz-card-top {
  display: flex;
  align-items: baseline;
  justify-content: flex-start;
  gap: 8px;
}
.ergo-advisor__quiz-card-kicker {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--ew-accent) !important;
}
.ergo-advisor__quiz-card-sub {
  font-size: 11px;
  font-weight: 500;
  color: var(--ew-400) !important;
}
.ergo-advisor__quiz-card-chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 6px;
}
.ergo-advisor__quiz-chip {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.25;
  color: var(--ew-accent-hover) !important;
  background: #e8f3f7;
  border: 1px solid #cfe3ec;
}
.ergo-advisor__quiz-chip.is-soft {
  font-weight: 500;
  color: var(--ew-800) !important;
  background: #f3f4f6;
  border-color: #e5e7eb;
}

.ergo-advisor__msg--assistant .ergo-advisor__bubble {
  background: #f1f1f1;
  color: #222;
  border-bottom-left-radius: 6px;
}

/* Markdown 富文本(v-html 内容需 :deep 才能命中) */
/* white-space: normal 覆盖气泡的 pre-wrap,避免 marked 输出的换行被渲染成空白行 */
.ergo-advisor__md {
  white-space: normal;
  line-height: 1.5;
}
.ergo-advisor__md :deep(p) {
  margin: 0 0 4px;
}
.ergo-advisor__md :deep(p:last-child) {
  margin-bottom: 0;
}
/* 兜底:无论最后一个元素是段落/列表/代码块/表格/引用,底部都不留 margin */
.ergo-advisor__md :deep(> :last-child) {
  margin-bottom: 0;
}
/* 兜底:marked 若在末尾输出 <br>,不占行 */
.ergo-advisor__md :deep(br:last-child) {
  display: none;
}
.ergo-advisor__md :deep(a) {
  color: #3f7f97;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.ergo-advisor__md :deep(table) {
  width: 100%;
  margin: 6px 0;
  border-collapse: collapse;
  font-size: 12px;
}
.ergo-advisor__md :deep(th),
.ergo-advisor__md :deep(td) {
  padding: 4px 8px;
  border: 1px solid #e5e7eb;
  text-align: left;
}
.ergo-advisor__md :deep(th) {
  background: #f9fafb;
  font-weight: 600;
}
.ergo-advisor__md :deep(ul),
.ergo-advisor__md :deep(ol) {
  margin: 4px 0;
  padding-left: 18px;
}
.ergo-advisor__md :deep(li) {
  margin: 2px 0;
}
.ergo-advisor__md :deep(code) {
  padding: 1px 4px;
  border-radius: 4px;
  background: #e7e7e7;
  font-size: 12px;
}
.ergo-advisor__md :deep(pre) {
  margin: 6px 0;
  padding: 8px 10px;
  border-radius: 8px;
  background: #222;
  color: #fff;
  overflow-x: auto;
}
.ergo-advisor__md :deep(blockquote) {
  margin: 6px 0;
  padding-left: 10px;
  border-left: 3px solid #b7d6e2;
  color: #555;
}

.ergo-advisor__cursor {
  display: inline-block;
  width: 2px;
  height: 1em;
  margin-left: 2px;
  vertical-align: text-bottom;
  background: var(--ew-accent);
  animation: ergo-advisor-blink 0.8s step-start infinite;
}

@keyframes ergo-advisor-blink {
  50% {
    opacity: 0;
  }
}

/* Thinking 加载态:三个品牌蓝跳动圆点 */
.ergo-advisor__thinking {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 12px 14px;
  border-radius: 16px;
  background: #f1f1f1;
  border-bottom-left-radius: 6px;
}

.ergo-advisor__thinking-text {
  margin-left: 4px;
  font-size: 13px;
  color: var(--ew-accent);
}

.ergo-advisor__dot {
  flex: none;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--ew-accent);
  animation: ergo-advisor-dot 1.2s ease-in-out infinite;
}

.ergo-advisor__dot:nth-child(2) {
  animation-delay: 0.15s;
}
.ergo-advisor__dot:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes ergo-advisor-dot {
  0%,
  60%,
  100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  30% {
    transform: translateY(-4px);
    opacity: 1;
  }
}

/* 超时新会话条(过期文案 + 描边按钮,显示在对话底部替换输入区) */
.ergo-advisor__timeout {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 12px 12px;
  background: #fff;
}

.ergo-advisor__timeout-text {
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
  color: #8a8a8a;
  text-align: center;
}

.ergo-advisor__timeout-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px;
  border: 2px solid var(--ew-accent);
  border-radius: 16px;
  background: #fff;
  color: var(--ew-accent);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.ergo-advisor__timeout-btn:hover {
  background: #f0f7fa;
}

.ergo-advisor__timeout-btn svg {
  width: 18px;
  height: 18px;
}

/* quiz 软入口:欢迎语下方的轻量小字入口(对齐 demo .ei-soft-entry,不抢戏) */
.ergo-advisor__quiz-entry {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  align-self: flex-start;
  margin: 0 0 2px 28px; /* 对齐助手气泡左缘(本组件 avatar + gap) */
  max-width: min(88%, 320px);
  padding: 0 2px;
  /* 淡入并轻微上移:入口挂到锚定消息下方时柔和出现(对齐 demo 的 eiFadeIn) */
  animation: ergo-advisor-entry-in 0.35s ease both;
}

@keyframes ergo-advisor-entry-in {
  from {
    opacity: 0;
    transform: translateY(4px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

.ergo-advisor__quiz-link {
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  color: var(--ew-accent);
  font-size: 11px;
  font-style: italic;
  font-weight: 400;
  line-height: 1.3;
  letter-spacing: -0.01em;
  text-align: left;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
  cursor: pointer;
  transition: color 0.15s ease, opacity 0.15s ease;
}

/* 触摸设备没有真正的 hover;用 media query 包裹,iOS 上点过之后不会把 hover 态粘住 */
@media (hover: hover) {
  .ergo-advisor__quiz-link:hover {
    color: var(--ew-accent-hover);
  }
}

.ergo-advisor__quiz-link-hint {
  font-size: 11px;
  font-style: italic;
  font-weight: 400;
  line-height: 1.3;
  letter-spacing: -0.01em;
  color: var(--ew-400);
  cursor: default;
}

/* quiz 抽屉:覆盖面板内部,从底部弹起,几乎占满面板高度(对齐 demo 的 max-height:94%) */
.ergo-advisor__quiz-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background: rgb(0 0 0 / 25%);
}

.ergo-advisor__quiz-drawer {
  display: flex;
  flex-direction: column;
  max-height: 94%; /* 内容自适应:短问卷自动收矮,内容多时才顶到上限并滚动 */
  overflow: hidden;
  border-radius: 16px 16px 0 0;
  background: #fff;
  box-shadow: 0 -4px 24px rgb(0 0 0 / 8%);
}

/* 视口偏矮时进一步放松到全高(demo 的 max-height:650px 分支) */
@media (max-height: 650px) {
  .ergo-advisor__quiz-drawer {
    max-height: 100%;
  }
}

.ergo-advisor__quiz-grabber {
  flex-shrink: 0;
  width: 36px;
  height: 4px;
  margin: 10px auto 4px;
  border-radius: 2px;
  background: #d0d7dd;
}

/* quiz 头部:Quick match kicker + 关闭按钮 */
.ergo-advisor__quiz-head {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 16px 6px;
}

/* kicker:Quick match(抽屉顶部为弱化灰字,与气泡卡片上的品牌蓝 kicker 区分) */
.ergo-advisor__quiz-kicker {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ew-400);
}

/* 进度行:圆点(当前 on 高亮 / 已答 done) + Question X of N */
.ergo-advisor__quiz-progress {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px 2px;
}

.ergo-advisor__quiz-dots {
  display: flex;
  align-items: center;
  gap: 5px;
}

.ergo-advisor__quiz-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--ew-200);
  transition: background 0.2s ease, transform 0.2s ease;
}

.ergo-advisor__quiz-dot.on {
  background: var(--ew-accent);
  transform: scale(1.15);
}

/* 已作答但非当前题:实心浅蓝,与「当前题」和「未答」区分 */
.ergo-advisor__quiz-dot.done {
  background: var(--ew-accent);
  opacity: 0.45;
}

.ergo-advisor__quiz-progress-label {
  font-size: 11px;
  color: var(--ew-400);
}

.ergo-advisor__quiz-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: var(--ew-100);
  color: var(--ew-700);
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
}

@media (hover: hover) {
  .ergo-advisor__quiz-close:hover {
    background: var(--ew-200);
  }
}

.ergo-advisor__quiz-body {
  /* 按内容撑开、超出上限时被压缩滚动。不用 flex:1 —— 抽屉高度为 auto 时
     flex-basis:0 会让它塌成 0 高(ei-optlist 用的是同一套 flex-shrink 方案) */
  flex: 0 1 auto;
  min-height: 0;
  overflow-x: hidden; /* 横向滚动只允许发生在 Bed size 轨道内 */
  overflow-y: auto;
  -webkit-overflow-scrolling: touch; /* iOS:内容区惯性滚动(对齐 demo 的 ei-optlist) */
  padding: 12px 16px 16px;
}

/* 末题 how_far:去掉内容区底部留白,让选项列表贴近底部，让页面没有滚动条 */
.ergo-advisor__quiz-body.is-flush-bottom {
  padding-bottom: 8px;
}

/* quiz 题干(E 盘 qtext:margin 0,间距交给下方的提示/计数与选项列表) */
.ergo-advisor__quiz-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--ew-900);
}

/* quiz 选项列表 */
.ergo-advisor__quiz-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
}

/* quiz 单个选项(整行可点) */
.ergo-advisor__quiz-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid #d0d7dd;
  border-radius: 10px;
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

@media (hover: hover) {
  .ergo-advisor__quiz-option:hover {
    border-color: var(--ew-accent);
    background: #f0f7fa;
  }
}

.ergo-advisor__quiz-option.is-active {
  border-color: var(--ew-accent);
  background: #eaf2f6;
}

/* quiz 选项指示圆点(radio)/方框(check) */
.ergo-advisor__quiz-option-dot {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  border: 2px solid #b0bcc4;
  box-sizing: border-box;
}

.ergo-advisor__quiz-option-dot.is-radio {
  border-radius: 50%;
}

.ergo-advisor__quiz-option-dot.is-check {
  border-radius: 4px;
}

.ergo-advisor__quiz-option.is-active .ergo-advisor__quiz-option-dot {
  border-color: var(--ew-accent);
  background: var(--ew-accent);
  box-shadow: inset 0 0 0 3px #fff;
}

.ergo-advisor__quiz-option-text {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  line-height: 1.45;
  color: var(--ew-800);
  word-break: break-word;
}

/* 第一题(8 个短选项)改为两列紧凑排布,避免拉成一条长清单
 * (对齐 demo readability.css 里 [data-question="sleep_change"] 的规则) */
.ergo-advisor__quiz-options.is-pair {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  align-content: start;
}

.ergo-advisor__quiz-options.is-pair .ergo-advisor__quiz-option {
  position: relative;
  align-items: center;
  min-height: 66px;
  padding: 9px 9px 9px 29px;
}

.ergo-advisor__quiz-options.is-pair .ergo-advisor__quiz-option-dot {
  position: absolute;
  left: 10px;
  top: 14px;
  width: 13px;
  height: 13px;
  margin-top: 0;
  border-width: 1.5px;
}

.ergo-advisor__quiz-options.is-pair .ergo-advisor__quiz-option.is-active .ergo-advisor__quiz-option-dot {
  box-shadow: inset 0 0 0 1px #fff;
}

.ergo-advisor__quiz-options.is-pair .ergo-advisor__quiz-option-text {
  font-size: 12px;
  line-height: 1.45;
}

/* Recommended 角标(题库预留能力,当前四题均未标记 recommended) */
.ergo-advisor__quiz-rec {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 999px;
  background: #e6f1f5;
  color: var(--ew-accent);
  font-size: 10px;
  font-weight: 700;
  vertical-align: 1px;
}

/* quiz 提示语(题干下方小字,如 Pick as many as you like) */
.ergo-advisor__quiz-hint {
  margin: 4px 0 0;
  font-size: 11.5px;
  line-height: 1.4;
  color: var(--ew-400);
}

/* quiz 选择计数:N selected / No selection yet(品牌蓝小字,紧跟提示语) */
.ergo-advisor__quiz-count {
  display: block;
  min-height: 16px;
  margin: 3px 0 0;
  font-size: 11px;
  line-height: 16px;
  color: var(--ew-accent-hover);
}

/* 选项下方的小字描述(how_far 各选项的 desc) */
.ergo-advisor__quiz-option-desc {
  display: block;
  margin-top: 3px;
  font-size: 11px;
  line-height: 1.4;
  color: var(--ew-400);
}

/* 末题附加:Bed size(可选) —— 标题右侧一对箭头,控制下方一行选项横向滚动 */
.ergo-advisor__quiz-size {
  margin: 8px 0 0;
  padding: 6px 0 0;
  min-width: 0;
}

/* 标题行:左侧「Bed size + Optional」,右侧一对翻页箭头 */
.ergo-advisor__quiz-size-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.ergo-advisor__quiz-size-legend {
  font-size: 13px;
  font-weight: 600;
  color: var(--ew-900); 
}

.ergo-advisor__quiz-size-optional {
  margin-left: 6px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ew-400);
}

/* 提示行:怎么浏览尺寸(对齐 demo 的 advisor-size-picker p) */
.ergo-advisor__quiz-size-hint {
  margin: 0 0 4px;
  font-size: 11.5px;
  line-height: 1.45;
  color: var(--ew-400);
}

.ergo-advisor__quiz-size-nav {
  display: flex;
  flex-shrink: 0;
  gap: 6px;
}

.ergo-advisor__quiz-size-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid var(--ew-200);
  border-radius: 8px;
  background: #fff;
  color: var(--ew-400);
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease;
}

@media (hover: hover) {
  .ergo-advisor__quiz-size-arrow:hover:not(:disabled) {
    border-color: var(--ew-300);
    color: var(--ew-700);
  }
}

/* 轨道已滚到两端时对应箭头置灰(对齐 demo 的 syncArrows) */
.ergo-advisor__quiz-size-arrow:disabled {
  opacity: 0.35;
  cursor: default;
}

/* 一行尺寸选项,横向滚动(方角卡片,与上方题目选项同一视觉语言) */
.ergo-advisor__quiz-size-track {
  position: relative; /* 让 chip.offsetLeft 相对本轨道计算(Bed size 选中居中用) */
  display: flex;
  gap: 8px;
  min-width: 0; /* 缺它时选项会撑破轨道,横向滚动条会跑到整个抽屉上 */
  padding: 1px 0 4px;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  -webkit-overflow-scrolling: touch; /* iOS:横滑尺寸轨道时带惯性 */
  scrollbar-width: thin;
  scrollbar-color: #bdd6e0 transparent;
}

.ergo-advisor__quiz-size-track::-webkit-scrollbar {
  height: 4px;
}

.ergo-advisor__quiz-size-track::-webkit-scrollbar-thumb {
  background: #bdd6e0;
  border-radius: 4px;
}

.ergo-advisor__quiz-size-chip {
  flex-shrink: 0;
  padding: 6px 14px;
  border: 1px solid #d0d7dd;
  border-radius: 10px;
  background: #fff;
  color: var(--ew-800);
  font-size: 12px;
  line-height: 1.3;
  white-space: nowrap;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease;
}

@media (hover: hover) {
  .ergo-advisor__quiz-size-chip:hover {
    border-color: var(--ew-accent);
    background: #f0f7fa;
  }
}

.ergo-advisor__quiz-size-chip.is-active {
  border-color: var(--ew-accent);
  background: #eaf2f6;
  color: var(--ew-accent);
}

/* 末题摘要:未答数量 + 初始匹配提示 + Review 跳缺失题 */
.ergo-advisor__quiz-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
  padding: 8px 10px;
  border-radius: 10px;
  background: #f5f8fa;
}

.ergo-advisor__quiz-summary strong {
  font-size: 12px;
  font-weight: 600;
  color: var(--ew-700);
}

.ergo-advisor__quiz-summary.is-complete {
  background: #e9f4f8;
}

.ergo-advisor__quiz-summary.is-complete strong {
  color: var(--ew-accent);
}

.ergo-advisor__quiz-summary-review {
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  color: var(--ew-accent);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

@media (hover: hover) {
  .ergo-advisor__quiz-summary-review:hover {
    text-decoration: underline;
  }
}

/* quiz 底部:左侧 skip + 右侧翻页/提交 */
.ergo-advisor__quiz-footer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 16px 14px;
  border-top: 1px solid var(--ew-200);
}

/* skip for now:灰色文字链接 */
.ergo-advisor__quiz-skip {
  padding: 0;
  border: none;
  background: none;
  color: var(--ew-400);
  font-size: 13px;
  cursor: pointer;
}

@media (hover: hover) {
  .ergo-advisor__quiz-skip:hover {
    text-decoration: underline;
    color: var(--ew-700);
  }
}

/* 右侧导航按钮组:←/→ 圆形 + 主按钮胶囊 */
.ergo-advisor__quiz-nav {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ergo-advisor__quiz-navbtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid var(--ew-200);
  border-radius: 50%;
  background: #fff;
  color: var(--ew-400);
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

@media (hover: hover) {
  .ergo-advisor__quiz-navbtn:hover:not(:disabled) {
    border-color: var(--ew-300);
    color: var(--ew-700);
  }
}

.ergo-advisor__quiz-navbtn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* 恒定 120×36:文案会在 Continue / See my match / See initial fit 之间切换,
 * 且各浏览器给 <button> 的默认字体不同 —— 不约束住,宽度就会跟着文案和浏览器漂移。
 *  - flex:none        不伸不缩(默认 flex-shrink:1 会在空间不足时把 120px 压小)
 *  - min-width:120px  覆盖 flex item 的 min-width:auto(内容最小宽度会顶开 width)
 *  - box-sizing       padding 计入 120px,总宽就是 120px 而非 122px
 *  - font-family/line-height  <button> 不继承 .ergo-advisor 的字体(UA 样式表),
 *                    不声明则各浏览器用各自的默认按钮字体,文本宽度与行高都不同 */
.ergo-advisor__quiz-primary {
  flex: none;
  box-sizing: border-box;
  width: 120px;
  min-width: 120px;
  white-space: nowrap;
  padding: 10px 1px;
  border: none;
  border-radius: 999px;
  background: var(--ew-accent);
  color: #fff;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.2;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgb(91 158 185 / 28%);
  transition: background-color 0.2s ease, transform 0.12s ease;
}

@media (hover: hover) {
  .ergo-advisor__quiz-primary:hover:not(:disabled) {
    background: var(--ew-accent-hover);
  }
}

.ergo-advisor__quiz-primary:active:not(:disabled) {
  transform: scale(0.98);
}

.ergo-advisor__quiz-primary:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  box-shadow: none;
}

/* 输入区 */
.ergo-advisor__inputbar {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px 10px;
}

.ergo-advisor__inputrow {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}

.ergo-advisor__input-disclaimer {
  margin: 0;
  font-size: 11px;
  line-height: 1.4;
  color: #9ca3af;
  text-align: center;
}

.ergo-advisor__input-wrap {
  position: relative;
  flex: 1;
  display: flex;
  box-sizing: border-box;
  padding: 10px 0 10px 14px;
  border: 1px solid #d9d9d9;
  border-radius: 16px;
  background: #fff;
}

.ergo-advisor__input {
  flex: 1;
  resize: none;
  box-sizing: border-box;
  padding: 0 44px 0 0;
  border: none;
  font: inherit;
  font-size: 14px;
  line-height: 21px;
  color: #222;
  background: transparent;
  outline: none;
  /* 隐藏滚动条(及其上下箭头),多行内容仍可用滚轮/键盘滚动 */
  overflow-y: auto;
  scrollbar-width: none;
}

.ergo-advisor__input::placeholder {
  color: #a8a8a8;
}

.ergo-advisor__input::-webkit-scrollbar {
  display: none;
}

.ergo-advisor__input-wrap:focus-within {
  border-color: var(--ew-accent);
}

.ergo-advisor__input:disabled {
  color: #000000;
  cursor: not-allowed;
}

.ergo-advisor__send {
  position: absolute;
  top: 50%;
  right: 6px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--ew-accent);
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

/* :hover 浅蓝背景 + 图标加深 */
.ergo-advisor__send:hover:not(:disabled) {
  background: rgb(91 158 185 / 10%);
  color: #3f7f97;
}

.ergo-advisor__send:active:not(:disabled) {
  transform: translateY(-50%) scale(0.9);
}

.ergo-advisor__send:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.ergo-advisor__send-icon {
  width: 18px;
  height: 18px;
}

/* ============================ End Chat 确认层(底部弹出,) ============================ */
.ergo-advisor__confirm-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background: rgb(0 0 0 / 25%);
}

.ergo-advisor__confirm {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px 16px 20px;
  border-radius: 18px 18px 0 0;
  background: #fff;
  box-shadow: 0 -4px 24px rgb(0 0 0 / 8%);
}

.ergo-advisor__confirm-danger {
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 12px;
  background: #e53935;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.ergo-advisor__confirm-danger:hover {
  background: #d32f2f;
}

.ergo-advisor__confirm-ghost {
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 12px;
  background: #fff;
  color: #222;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.ergo-advisor__confirm-ghost:hover {
  background: #f3f4f6;
}

/* ============================ 动画 ============================ */
.ergo-advisor-panel-enter-active,
.ergo-advisor-panel-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
  transform-origin: bottom right;
}
.ergo-advisor-panel-enter-from,
.ergo-advisor-panel-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.96);
}

.ergo-advisor-bubble-enter-active,
.ergo-advisor-bubble-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.ergo-advisor-bubble-enter-from,
.ergo-advisor-bubble-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

.ergo-advisor-fade-enter-active,
.ergo-advisor-fade-leave-active {
  transition: opacity 0.18s ease;
}
.ergo-advisor-fade-enter-from,
.ergo-advisor-fade-leave-to {
  opacity: 0;
}

/* quiz 抽屉:遮罩淡入 + 抽屉从底部滑入 */
.ergo-advisor-drawer-enter-active,
.ergo-advisor-drawer-leave-active {
  transition: opacity 0.25s ease;
}
.ergo-advisor-drawer-enter-active .ergo-advisor__quiz-drawer,
.ergo-advisor-drawer-leave-active .ergo-advisor__quiz-drawer {
  transition: transform 0.25s ease;
}
.ergo-advisor-drawer-enter-from,
.ergo-advisor-drawer-leave-to {
  opacity: 0;
}
.ergo-advisor-drawer-enter-from .ergo-advisor__quiz-drawer,
.ergo-advisor-drawer-leave-to .ergo-advisor__quiz-drawer {
  transform: translateY(100%);
}
</style>
